import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { formatTimestamp, formatDateTime, formatWeekdayMonthDay } from "@/lib/date-utils";
import { enumerateEventDays, isDayPassAvailable } from "@satyrsmc/shared/lib/event-days";
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import { trpc } from "@/trpc";
import { useUrlSearchSync, urlFilterState } from "@/queries/hooks";
import { toast } from "sonner";
import { Button } from "@satyrsmc/shared/components";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  ArrowLeft,
  CheckCircle,
  ChevronDown,
  Clock,
  DollarSign,
  Download,
  Mail,
  MoreVertical,
  Pencil,
  RotateCcw,
  Search,
  Send,
  Trash2,
  UserPlus,
  Users,
  XCircle,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { MultiSelectFilter } from "@/components/ui/multi-select-filter";
import { useConfirm } from "@/components/ui/confirm-dialog";
import {
  ContactSelectCombobox,
  type ContactOption,
} from "@/components/users/ContactSelectCombobox";
import type { TravelMode, TshirtSize } from "@satyrsmc/shared/lib/enums";
import {
  AttendeeStatus,
  ContactSortField,
  ContactStatusFilter,
  EventStatus,
  EventType,
  PaymentMethod,
  PaymentStatus,
  RsvpLogCode,
  SortDirection,
} from "@satyrsmc/shared/lib/enums";
import { isEventPast } from "@/lib/eventUtils";
import type { EventAttendeeAdmin } from "@satyrsmc/shared/dto/admin/event";
import { useAuth } from "@/state/AuthContext";
import { downloadAttendeesCsv } from "./attendeeCsv";
import { EmailAttendeesDialog } from "./EmailAttendeesDialog";
import { SendPostEventEmailDialog } from "./SendPostEventEmailDialog";
import { SendPaymentReminderDialog } from "./SendPaymentReminderDialog";
import {
  TRAVEL_LABELS,
  type AttendeeFormState,
  defaultFormState,
  AttendeeFormFields,
  EditAttendeeDialog,
} from "./AttendeeEditDialog";

// --- Labels ---

const PAYMENT_LABELS: Record<PaymentStatus, string> = {
  [PaymentStatus.NotRequired]: "N/A",
  [PaymentStatus.Pending]: "Pending",
  [PaymentStatus.Confirmed]: "Paid",
  [PaymentStatus.RefundRequested]: "Refund Req.",
  [PaymentStatus.Refunded]: "Refunded",
};

const PAYMENT_COLORS: Record<PaymentStatus, string> = {
  [PaymentStatus.NotRequired]: "text-muted-foreground",
  [PaymentStatus.Pending]: "text-yellow-600",
  [PaymentStatus.Confirmed]: "text-green-600",
  [PaymentStatus.RefundRequested]: "text-orange-600",
  [PaymentStatus.Refunded]: "text-muted-foreground",
};

const PAYMENT_METHOD_LABELS: Record<PaymentMethod, string> = {
  [PaymentMethod.Check]: "Check",
  [PaymentMethod.Cash]: "Cash",
  [PaymentMethod.Zelle]: "Zelle",
  [PaymentMethod.CreditCard]: "Credit Card",
};

// invitation/token are legacy DB values kept for display; only Auth and Admin
// are in the current enum. Historical NULL rows were backfilled to "admin"
// (migration 1800000059000), but the plain free-event self-RSVP path
// (MemberEventsService.rsvp's simple insert) still never sets this column,
// so new NULL rows can still appear going forward — displayed as "None",
// handled separately below.
const REGISTRATION_METHOD_LABELS: Record<string, string> = {
  auth: "Self",
  admin: "Admin",
  invitation: "Invitation",
  token: "Event Link",
};

const LOG_CODE_LABELS: Record<RsvpLogCode, string> = {
  [RsvpLogCode.Registered]: "Registered",
  [RsvpLogCode.MatchedToContact]: "Matched to contact",
  [RsvpLogCode.NewContactCreated]: "New contact created",
  [RsvpLogCode.PaymentConfirmed]: "Payment confirmed",
  [RsvpLogCode.PaymentReminderSent]: "Payment reminder sent",
  [RsvpLogCode.RefundRequested]: "Refund requested",
  [RsvpLogCode.RefundProcessed]: "Refund processed",
  [RsvpLogCode.Cancelled]: "Cancelled",
  [RsvpLogCode.AdminCancelled]: "Admin cancelled",
  [RsvpLogCode.AccountLinked]: "Account linked",
  [RsvpLogCode.StatusChanged]: "Status changed",
  [RsvpLogCode.Deleted]: "Deleted",
};

// --- Add Attendee Dialog ---

type AddAttendeeMode = "invite" | "manual" | null;

function ModeSelector({ onSelect }: { onSelect: (mode: "invite" | "manual") => void }) {
  return (
    <div className="grid grid-cols-2 gap-3 py-2">
      <button
        type="button"
        className="flex flex-col items-start gap-1.5 rounded-lg border border-border p-4 text-left transition hover:border-primary hover:bg-accent"
        onClick={() => onSelect("invite")}
      >
        <span className="font-medium text-sm">Invite Contact</span>
        <span className="text-xs text-muted-foreground">
          Send an invite so they can register themselves.
        </span>
      </button>
      <button
        type="button"
        className="flex flex-col items-start gap-1.5 rounded-lg border border-border p-4 text-left transition hover:border-primary hover:bg-accent"
        onClick={() => onSelect("manual")}
      >
        <span className="font-medium text-sm">Manual RSVP</span>
        <span className="text-xs text-muted-foreground">
          Enter all details and register on their behalf.
        </span>
      </button>
    </div>
  );
}

const INVITE_PAGE_SIZE = 25;

function InviteContactPanel({
  eventId,
  existingContactIds,
  onDone,
}: {
  eventId: string;
  existingContactIds: Set<string>;
  onDone: () => void;
}) {
  const [selectedContactId, setSelectedContactId] = useState<string | null>(null);
  const [createUserAccount, setCreateUserAccount] = useState(true);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [page, setPage] = useState(1);
  const [accumulatedContacts, setAccumulatedContacts] = useState<ContactOption[]>([]);

  const handleSearchChange = (q: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedQuery(q);
      setPage(1);
      setAccumulatedContacts([]);
    }, 300);
  };

  const { data, isFetching } = trpc.admin.contacts.list.useQuery({
    q: debouncedQuery || undefined,
    limit: INVITE_PAGE_SIZE,
    page,
    status: ContactStatusFilter.Active,
    sort: ContactSortField.Name,
    sortDir: SortDirection.Asc,
    forEventInvite: true,
  });

  useEffect(() => {
    if (!data) return;
    const incoming: ContactOption[] = data.contacts
      .filter((c) => !existingContactIds.has(c.id))
      .map((c) => ({
        id: c.id,
        first_name: c.first_name ?? null,
        last_name: c.last_name ?? null,
        display_name: c.display_name,
        email: c.emails?.find((e) => e.is_primary)?.email ?? c.emails?.[0]?.email ?? null,
      }));
    if (data.page === 1) {
      setAccumulatedContacts(incoming);
    } else {
      setAccumulatedContacts((prev) => {
        const existingIds = new Set(prev.map((c) => c.id));
        return [...prev, ...incoming.filter((c) => !existingIds.has(c.id))];
      });
    }
  }, [data, existingContactIds]);

  const hasMore = data != null && data.contacts.length === INVITE_PAGE_SIZE;

  const handleLoadMore = () => {
    if (!isFetching && hasMore) setPage((p) => p + 1);
  };

  const { data: contactUser, isLoading: isUserLoading } = trpc.admin.users.getByContactId.useQuery(
    { contact_id: selectedContactId ?? "" },
    { enabled: selectedContactId !== null },
  );

  const contactHasUser =
    selectedContactId !== null
      ? isUserLoading
        ? null
        : contactUser !== null && contactUser !== undefined
      : null;

  // Reset checkbox to checked whenever a new contact is selected.
  useEffect(() => {
    setCreateUserAccount(true);
  }, [selectedContactId]);

  const options = accumulatedContacts;

  const inviteMutation = trpc.admin.events.sendEventInvite.useMutation({
    onSuccess: () => {
      toast.success("Invite sent.");
      onDone();
    },
    onError: (err) => {
      toast.error(err.message ?? "Failed to send invite.");
    },
  });

  const canSend =
    selectedContactId !== null &&
    !isUserLoading &&
    !(contactHasUser === false && !createUserAccount);

  return (
    <div className="space-y-4">
      <ContactSelectCombobox
        contacts={options}
        value={selectedContactId}
        onChange={setSelectedContactId}
        onSearchChange={handleSearchChange}
        onLoadMore={handleLoadMore}
        hasMore={hasMore}
        isLoading={isFetching}
        placeholder="Search contacts..."
        label="Invite Contact"
      />
      {selectedContactId && !isUserLoading && contactHasUser === false && (
        <div className="rounded-md border border-border bg-muted/50 p-3 space-y-2">
          <p className="text-xs text-muted-foreground">
            This contact doesn&apos;t have a user account yet.
          </p>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              id="create-user-account"
              checked={createUserAccount}
              onChange={(e) => setCreateUserAccount(e.target.checked)}
              className="rounded"
            />
            <span>Create a user account so they can sign in with the invite link.</span>
          </label>
        </div>
      )}
      <DialogFooter>
        <Button
          onClick={() => {
            if (selectedContactId)
              inviteMutation.mutate({
                eventId,
                contactId: selectedContactId,
                create_user_account: contactHasUser === false ? createUserAccount : undefined,
              });
          }}
          disabled={!canSend || inviteMutation.isPending}
        >
          Send Invite
        </Button>
      </DialogFooter>
    </div>
  );
}

function ManualRsvpPanel({
  eventId,
  eventType,
  dayPassDays,
  existingContactIds,
  onDone,
}: {
  eventId: string;
  eventType: EventType | null;
  dayPassDays: string[];
  existingContactIds: Set<string>;
  onDone: () => void;
}) {
  const [contactId, setContactId] = useState<string | null>(null);
  const [form, setForm] = useState<AttendeeFormState>(defaultFormState);
  const [sendConfirmation, setSendConfirmation] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [page, setPage] = useState(1);
  const [accumulatedContacts, setAccumulatedContacts] = useState<ContactOption[]>([]);

  const handleSearchChange = (q: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedQuery(q);
      setPage(1);
      setAccumulatedContacts([]);
    }, 300);
  };

  const { data, isFetching: isLoading } = trpc.admin.contacts.list.useQuery({
    q: debouncedQuery || undefined,
    limit: INVITE_PAGE_SIZE,
    page,
    status: ContactStatusFilter.Active,
    sort: ContactSortField.Name,
    sortDir: SortDirection.Asc,
    forEventInvite: true,
  });

  useEffect(() => {
    if (!data) return;
    const incoming: ContactOption[] = data.contacts
      .filter((c) => !existingContactIds.has(c.id))
      .map((c) => ({
        id: c.id,
        first_name: c.first_name,
        last_name: c.last_name,
        display_name: c.display_name,
        email:
          (c.emails ?? []).find((e) => e.is_primary)?.email ?? (c.emails ?? [])[0]?.email ?? null,
      }));
    if (data.page === 1) {
      setAccumulatedContacts(incoming);
    } else {
      setAccumulatedContacts((prev) => {
        const existingIds = new Set(prev.map((c) => c.id));
        return [...prev, ...incoming.filter((c) => !existingIds.has(c.id))];
      });
    }
  }, [data, existingContactIds]);

  const hasMoreManual = data != null && data.contacts.length === INVITE_PAGE_SIZE;

  const handleLoadMoreManual = () => {
    if (!isLoading && hasMoreManual) setPage((p) => p + 1);
  };

  const addManualMutation = trpc.admin.events.addAttendeeManual.useMutation({
    onSuccess: () => {
      toast.success("Attendee added.");
      onDone();
    },
    onError: (err) => {
      toast.error(err.message ?? "Failed to add attendee.");
    },
  });

  const options = accumulatedContacts;

  const isCampout = eventType === EventType.Badger || eventType === EventType.PioneerRun;
  const isRides = eventType === EventType.Rides;

  const selectedContact = options.find((c) => c.id === contactId) ?? null;
  const selectedContactHasEmail = selectedContact?.email != null;

  const set = <K extends keyof AttendeeFormState>(key: K, val: AttendeeFormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: val }));

  const handleContactChange = (id: string | null) => {
    setContactId(id);
    const contact = options.find((c) => c.id === id);
    setSendConfirmation(contact?.email != null);
  };

  const handleSubmit = () => {
    if (!contactId) return;
    const needsPayment = form.payment_status !== PaymentStatus.NotRequired;
    const amountCents =
      needsPayment && form.payment_amount
        ? Math.round(parseFloat(form.payment_amount) * 100)
        : null;

    addManualMutation.mutate({
      eventId,
      contact_id: contactId,
      status: form.status,
      waiver_signed: form.waiver_signed,
      payment_status: form.payment_status,
      payment_method: form.payment_method ? (form.payment_method as PaymentMethod) : undefined,
      payment_amount_cents: amountCents ?? undefined,
      is_rider: isRides ? form.is_rider : undefined,
      dietary_restrictions: form.dietary_restrictions || undefined,
      campout_details: isCampout
        ? {
            tshirtSize: form.tshirt_size ? (form.tshirt_size as TshirtSize) : null,
            travelingBy: form.traveling_by ? (form.traveling_by as TravelMode) : null,
            club: form.club || null,
            pinHeader: form.pin_header,
            pinLadder: form.pin_ladder,
          }
        : undefined,
      attendance_date: form.attendance_date || null,
      checked_in: form.checked_in,
      notes: form.notes || null,
      send_confirmation: sendConfirmation || undefined,
    });
  };

  return (
    <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
      <ContactSelectCombobox
        contacts={options}
        value={contactId}
        onChange={handleContactChange}
        onSearchChange={handleSearchChange}
        onLoadMore={handleLoadMoreManual}
        hasMore={hasMoreManual}
        isLoading={isLoading}
        placeholder="Search contacts..."
        label="Contact"
      />
      <AttendeeFormFields form={form} set={set} eventType={eventType} dayPassDays={dayPassDays} />
      {selectedContactHasEmail && (
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input
            type="checkbox"
            checked={sendConfirmation}
            onChange={(e) => setSendConfirmation(e.target.checked)}
            className="rounded"
          />
          Send confirmation email to {selectedContact!.email}
        </label>
      )}
      <DialogFooter>
        <Button onClick={handleSubmit} disabled={!contactId || addManualMutation.isPending}>
          Add Attendee
        </Button>
      </DialogFooter>
    </div>
  );
}

function AddAttendeeDialog({
  eventId,
  eventType,
  dayPassDays,
  existingContactIds,
  open,
  onOpenChange,
  onAdded,
}: {
  eventId: string;
  eventType: EventType | null;
  dayPassDays: string[];
  existingContactIds: Set<string>;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAdded: () => void;
}) {
  const [mode, setMode] = useState<AddAttendeeMode>(null);

  const handleClose = () => {
    setMode(null);
    onOpenChange(false);
  };

  const handleDone = () => {
    onAdded();
    handleClose();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(val) => {
        if (!val) handleClose();
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {mode === "invite"
              ? "Invite Contact"
              : mode === "manual"
                ? "Manual RSVP"
                : "Add Attendee"}
          </DialogTitle>
        </DialogHeader>
        {mode === null && <ModeSelector onSelect={setMode} />}
        {mode === "invite" && (
          <InviteContactPanel
            eventId={eventId}
            existingContactIds={existingContactIds}
            onDone={handleDone}
          />
        )}
        {mode === "manual" && (
          <ManualRsvpPanel
            eventId={eventId}
            eventType={eventType}
            dayPassDays={dayPassDays}
            existingContactIds={existingContactIds}
            onDone={handleDone}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

// --- Confirm payment (send receipt / confirm only / cancel) ---

type ConfirmPaymentResult = "send_receipt" | "no_receipt" | "cancel";

function useConfirmPayment() {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const resolverRef = useRef<((result: ConfirmPaymentResult) => void) | null>(null);

  const confirmPayment = useCallback((msg: string): Promise<ConfirmPaymentResult> => {
    setMessage(msg);
    setOpen(true);
    return new Promise<ConfirmPaymentResult>((resolve) => {
      resolverRef.current = resolve;
    });
  }, []);

  const resolve = (result: ConfirmPaymentResult) => {
    setOpen(false);
    resolverRef.current?.(result);
    resolverRef.current = null;
  };

  const dialog = (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && resolve("cancel")}>
      <DialogContent showCloseButton={false}>
        <DialogHeader>
          <DialogTitle>Confirm payment</DialogTitle>
          <DialogDescription>{message}</DialogDescription>
        </DialogHeader>
        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="outline" onClick={() => resolve("cancel")}>
            Cancel
          </Button>
          <Button variant="outline" onClick={() => resolve("no_receipt")}>
            Confirm only
          </Button>
          <Button onClick={() => resolve("send_receipt")}>
            <CheckCircle className="size-3.5" />
            Confirm &amp; send receipt
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  return { confirmPayment, confirmPaymentDialog: dialog };
}

// --- Attendees Table ---

/** Small avatar used next to a name in the attendees table — a member photo
 * when available, otherwise an initial, matching the roster list page. */
function AttendeeAvatar({ name, photoUrl }: { name: string; photoUrl: string | null }) {
  if (photoUrl) {
    return <img src={photoUrl} alt="" className="size-8 rounded-full object-cover shrink-0" />;
  }
  return (
    <div className="size-8 shrink-0 rounded-full bg-muted flex items-center justify-center text-xs font-bold text-muted-foreground">
      {name.charAt(0).toUpperCase()}
    </div>
  );
}

const BULK_PAYMENT_LIMIT = 20;

function AttendeesTable({
  data,
  isWebmaster,
  selectedIds,
  onToggleSelect,
  onToggleSelectAllPending,
  onViewHistory,
  onEdit,
  onConfirmPayment,
  onSendReminder,
  onCancelRegistration,
  onProcessRefund,
  onUndoPaymentConfirmation,
  onDeleteRsvp,
}: {
  data: EventAttendeeAdmin[];
  isWebmaster: boolean;
  selectedIds: Set<string>;
  onToggleSelect: (id: string) => void;
  onToggleSelectAllPending: () => void;
  onViewHistory: (rsvpId: string, name: string) => void;
  onEdit: (attendee: EventAttendeeAdmin) => void;
  onConfirmPayment: (attendee: EventAttendeeAdmin) => void;
  onSendReminder: (attendee: EventAttendeeAdmin) => void;
  onCancelRegistration: (attendee: EventAttendeeAdmin) => void;
  onProcessRefund: (rsvpId: string, name: string) => void;
  onUndoPaymentConfirmation: (rsvpId: string, name: string) => void;
  onDeleteRsvp: (rsvpId: string, name: string) => void;
}) {
  const pendingRows = data.filter((a) => a.payment_status === PaymentStatus.Pending);
  const allPendingSelected =
    pendingRows.length > 0 && pendingRows.every((a) => selectedIds.has(a.id));

  return (
    <div className="space-y-2">
      <Table className="table-fixed min-w-[1220px]">
        <TableHeader>
          <TableRow>
            <TableHead className="w-8 max-sm:py-3">
              {pendingRows.length > 0 && (
                <input
                  type="checkbox"
                  className="rounded"
                  checked={allPendingSelected}
                  onChange={onToggleSelectAllPending}
                  aria-label="Select all pending payments"
                />
              )}
            </TableHead>
            <TableHead className="w-52 max-sm:py-3">Name</TableHead>
            <TableHead className="w-20 max-sm:py-3">Waiver</TableHead>
            <TableHead className="w-24 max-sm:py-3">Method</TableHead>
            <TableHead className="w-36 max-sm:py-3">Payment</TableHead>
            <TableHead className="w-24 max-sm:py-3">Pay. Type</TableHead>
            <TableHead className="w-28 max-sm:py-3">Attending</TableHead>
            <TableHead className="w-16 max-sm:py-3">Shirt</TableHead>
            <TableHead className="w-24 max-sm:py-3">Travel</TableHead>
            <TableHead className="w-24 max-sm:py-3">Club</TableHead>
            <TableHead className="w-32 max-sm:py-3">Registered</TableHead>
            <TableHead className="w-12 max-sm:py-3" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.length === 0 ? (
            <TableRow>
              <TableCell colSpan={12} className="py-6 text-center text-muted-foreground">
                No attendees match the current filters.
              </TableCell>
            </TableRow>
          ) : (
            data.map((a) => {
              const name = a.member?.name ?? a.contact?.display_name ?? "Unknown";
              const ps = a.payment_status;
              const isCancelled = a.status === AttendeeStatus.No;
              const canUndoPayment =
                ps === PaymentStatus.Confirmed &&
                a.payment_method !== null &&
                a.payment_method !== PaymentMethod.CreditCard;
              return (
                <TableRow key={a.id} className={isCancelled ? "opacity-60" : ""}>
                  <TableCell className="max-sm:py-3">
                    {ps === PaymentStatus.Pending && (
                      <input
                        type="checkbox"
                        checked={selectedIds.has(a.id)}
                        onChange={() => onToggleSelect(a.id)}
                        disabled={!selectedIds.has(a.id) && selectedIds.size >= BULK_PAYMENT_LIMIT}
                        className="rounded"
                        aria-label={`Select ${name} for bulk payment confirmation`}
                      />
                    )}
                  </TableCell>
                  <TableCell className="max-sm:py-3 overflow-hidden font-medium">
                    {a.contact_id ? (
                      <Link
                        to={`/admin/contacts/${a.contact_id}`}
                        className="flex items-center gap-2 hover:underline"
                        title={name}
                      >
                        <AttendeeAvatar name={name} photoUrl={a.member?.photo_url ?? null} />
                        <span className="truncate">{name}</span>
                      </Link>
                    ) : (
                      <div className="flex items-center gap-2">
                        <AttendeeAvatar name={name} photoUrl={a.member?.photo_url ?? null} />
                        <span className="truncate">{name}</span>
                      </div>
                    )}
                  </TableCell>
                  <TableCell
                    className={`max-sm:py-3 ${a.waiver_signed ? "text-green-600" : "text-red-600"}`}
                  >
                    {a.waiver_signed ? "Signed" : "No"}
                  </TableCell>
                  <TableCell className="text-muted-foreground max-sm:py-3">
                    {a.registration_method
                      ? (REGISTRATION_METHOD_LABELS[a.registration_method] ?? a.registration_method)
                      : "None"}
                  </TableCell>
                  <TableCell className="max-sm:py-3">
                    {ps ? (
                      <span
                        className={`inline-flex items-center gap-1 text-xs font-medium ${PAYMENT_COLORS[ps] ?? ""}`}
                      >
                        {PAYMENT_LABELS[ps] ?? ps}
                        {a.payment_amount_cents != null && ps !== PaymentStatus.NotRequired && (
                          <> (${(a.payment_amount_cents / 100).toFixed(2)})</>
                        )}
                        {ps === PaymentStatus.RefundRequested && (
                          <button
                            type="button"
                            title="Confirm refund"
                            className="ml-1 text-orange-500 hover:text-orange-700"
                            onClick={() => onProcessRefund(a.id, name)}
                          >
                            <CheckCircle className="size-3.5" />
                          </button>
                        )}
                      </span>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-muted-foreground text-xs max-sm:py-3">
                    {a.payment_method
                      ? (PAYMENT_METHOD_LABELS[a.payment_method] ?? a.payment_method)
                      : "—"}
                  </TableCell>
                  <TableCell className="text-muted-foreground max-sm:py-3">
                    {a.attendance_date ? formatWeekdayMonthDay(a.attendance_date) : "Full event"}
                  </TableCell>
                  <TableCell className="text-muted-foreground max-sm:py-3">
                    {a.campout_details?.tshirtSize ?? "—"}
                  </TableCell>
                  <TableCell className="text-muted-foreground max-sm:py-3">
                    {a.campout_details?.travelingBy
                      ? (TRAVEL_LABELS[a.campout_details.travelingBy] ??
                        a.campout_details.travelingBy)
                      : "—"}
                  </TableCell>
                  <TableCell className="text-muted-foreground max-sm:py-3">
                    {a.campout_details?.club ?? "—"}
                  </TableCell>
                  <TableCell className="text-muted-foreground max-sm:py-3">
                    {a.created_at ? formatTimestamp(a.created_at) : "—"}
                  </TableCell>
                  <TableCell className="max-sm:py-3">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 px-2 max-sm:h-9 max-sm:w-9 max-sm:px-0"
                        >
                          <MoreVertical className="size-3.5" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => onEdit(a)}>
                          <Pencil className="size-4 mr-2" />
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        {ps === PaymentStatus.Pending && (
                          <DropdownMenuItem onClick={() => onConfirmPayment(a)}>
                            <DollarSign className="size-4 mr-2" />
                            Confirm Payment
                          </DropdownMenuItem>
                        )}
                        {ps === PaymentStatus.Pending && (
                          <DropdownMenuItem onClick={() => onSendReminder(a)}>
                            <Mail className="size-4 mr-2" />
                            Send payment reminder
                          </DropdownMenuItem>
                        )}
                        {ps === PaymentStatus.RefundRequested && (
                          <DropdownMenuItem onClick={() => onProcessRefund(a.id, name)}>
                            <CheckCircle className="size-4 mr-2" />
                            Confirm refund
                          </DropdownMenuItem>
                        )}
                        {canUndoPayment && (
                          <DropdownMenuItem onClick={() => onUndoPaymentConfirmation(a.id, name)}>
                            <RotateCcw className="size-4 mr-2" />
                            Undo payment confirmation
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem onClick={() => onViewHistory(a.id, name)}>
                          <Clock className="size-4 mr-2" />
                          View history
                        </DropdownMenuItem>
                        {!isCancelled && (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              variant="destructive"
                              onClick={() => onCancelRegistration(a)}
                            >
                              <XCircle className="size-4 mr-2" />
                              Cancel registration
                            </DropdownMenuItem>
                          </>
                        )}
                        {isWebmaster && (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              className="text-destructive focus:text-destructive"
                              onClick={() => onDeleteRsvp(a.id, name)}
                            >
                              <Trash2 className="size-4 mr-2" />
                              Delete RSVP
                            </DropdownMenuItem>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
      <p className="text-xs text-muted-foreground">{data.length} attendee(s)</p>
    </div>
  );
}

// --- History Dialog ---

function HistoryDialog({
  rsvpId,
  name,
  open,
  onOpenChange,
}: {
  rsvpId: string;
  name: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { data: logs, isLoading } = trpc.admin.rsvps.getLogs.useQuery(
    { rsvpId },
    { enabled: open },
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>RSVP History — {name}</DialogTitle>
        </DialogHeader>
        {isLoading ? (
          <p className="text-sm text-muted-foreground py-4 text-center">Loading...</p>
        ) : logs && logs.length > 0 ? (
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {logs.map((log) => (
              <div key={log.id} className="flex items-start gap-3 text-sm">
                <span className="text-xs text-muted-foreground whitespace-nowrap pt-0.5">
                  {formatDateTime(log.createdAt)}
                </span>
                <div>
                  <span className="font-medium">
                    {LOG_CODE_LABELS[log.messageCode] ?? log.messageCode}
                  </span>
                  {log.message && <p className="text-xs text-muted-foreground">{log.message}</p>}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground py-4 text-center">No history available.</p>
        )}
      </DialogContent>
    </Dialog>
  );
}

// --- Helpers ---

/** Every URL param this page's filter bar reads — the single list
 * urlFilterState derives both "is anything filtered" and "Clear filters"
 * from, instead of those living as two separately hand-maintained copies. */
const FILTER_KEYS = [
  "q",
  "status",
  "waiver",
  "checkin",
  "method",
  "payment",
  "quick",
  "sort",
] as const;

type ToggleFilter = "members" | "first_time";

/** "Confirmed"/"Pending registrations"/"Cancelled (unpaid)"/"Pending refund"
 * aren't their own filter mode — they're shortcuts that set the same Status/Payment multi-
 * selects the dropdowns above expose, so the result stays visible and
 * adjustable there afterward rather than living in a separate hidden "quick"
 * state. None of them is a default: a bare visit to this page (no query
 * params at all) shows every attendee, unfiltered by Status or Payment —
 * that absence of restriction is the only "default" this page has, and it's
 * represented purely by the absence of the "status"/"payment" params, never
 * by a special-cased fallback value. Applying (or removing) a preset always
 * writes/deletes real params — see applyMultiParam. */
const REFUND_ONLY_PAYMENT = new Set<PaymentStatus>([PaymentStatus.RefundRequested]);
const PENDING_ONLY_PAYMENT = new Set<PaymentStatus>([PaymentStatus.Pending]);
const CONFIRMED_PAYMENT = new Set<PaymentStatus>([
  PaymentStatus.Confirmed,
  PaymentStatus.NotRequired,
]);
const NO_RESTRICTION_STATUS = new Set<AttendeeStatus>();
const NO_RESTRICTION_PAYMENT = new Set<PaymentStatus>();
const CONFIRMED_STATUS = new Set<AttendeeStatus>([AttendeeStatus.Yes]);
const CANCELLED_STATUS = new Set<AttendeeStatus>([AttendeeStatus.No]);

function setsEqual<T>(a: Set<T>, b: Set<T>): boolean {
  return a.size === b.size && [...a].every((v) => b.has(v));
}

/** Whether the current Status/Payment filters exactly match a preset (e.g.
 * Confirmed, Pending refund) — drives each quick-filter button's
 * active/inactive styling. Every preset follows this identical shape; this
 * is the one place that says so, instead of near-identical
 * setsEqual(...) && setsEqual(...) lines that'd need to be kept in sync by
 * hand as presets are added. */
function matchesPreset(
  currentPayment: Set<PaymentStatus>,
  currentStatus: Set<AttendeeStatus>,
  targetPayment: Set<PaymentStatus>,
  targetStatus: Set<AttendeeStatus>,
): boolean {
  return setsEqual(currentPayment, targetPayment) && setsEqual(currentStatus, targetStatus);
}

type SortBy = "recent" | "name";

/** Parses a comma-separated URL param into a Set. An absent param and an
 * explicit empty one both mean "no restriction" — there's no separate
 * default value to fall back to, so nothing distinguishes them. */
function parseMultiParam<T extends string>(raw: string | null): Set<T> {
  if (raw === null) return new Set();
  return new Set(raw.split(",").filter(Boolean) as T[]);
}

/** Returns a new URLSearchParams with a multi-select Set written back as a
 * comma list, or the param deleted entirely when the Set is empty — the
 * inverse of parseMultiParam. Never writes an explicit empty string: "no
 * restriction" and "absent" are the same state, so there's nothing to gain
 * from distinguishing them in the URL, and deleting keeps it clean. */
function applyMultiParam<T extends string>(
  params: URLSearchParams,
  key: string,
  values: Set<T>,
): URLSearchParams {
  const next = new URLSearchParams(params);
  if (values.size === 0) next.delete(key);
  else next.set(key, [...values].join(","));
  return next;
}

// --- Page ---

export function EventAttendeesPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const utils = trpc.useUtils();
  const { confirm, confirmDialog } = useConfirm();
  const { confirmPayment, confirmPaymentDialog } = useConfirmPayment();
  const { isWebmaster } = useAuth();
  const [historyTarget, setHistoryTarget] = useState<{ rsvpId: string; name: string } | null>(null);
  const [editTarget, setEditTarget] = useState<EventAttendeeAdmin | null>(null);
  const [reminderTarget, setReminderTarget] = useState<EventAttendeeAdmin | null>(null);
  const [addAttendeeOpen, setAddAttendeeOpen] = useState(false);
  const [emailAttendeesOpen, setEmailAttendeesOpen] = useState(false);
  const [postEventEmailOpen, setPostEventEmailOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());

  // --- Filter state from URL ---
  const rawQ = searchParams.get("q") ?? "";
  const statusFilter = parseMultiParam<AttendeeStatus>(searchParams.get("status"));
  const waiverFilter = searchParams.get("waiver") as "signed" | "unsigned" | null;
  const checkinFilter = searchParams.get("checkin") as "checked_in" | "not_checked_in" | null;
  const methodFilter = searchParams.get("method") as string | null;
  const paymentFilter = parseMultiParam<PaymentStatus>(searchParams.get("payment"));
  // "quick" is its own filter dimension — a simple toggle, distinct from the
  // Status/Payment presets below (Confirmed/Pending registrations/Cancelled
  // (unpaid)/Pending refund) despite both groups sharing the same "Quick:"
  // button row. Naming it ToggleFilter rather than the more obvious
  // "QuickFilter" is deliberate: that name is reserved for
  // quickFilterButtons, the array covering *all* the buttons in the row, and
  // reusing it here would make it look like this one variable was the row's
  // single source of truth when it's really only 2 of them.
  const toggleFilter = searchParams.get("quick") as ToggleFilter | null;
  const sortBy: SortBy = searchParams.get("sort") === "name" ? "name" : "recent";

  // "Active" here means the Status/Payment filters currently equal the
  // preset these shortcut buttons apply — not a separate quick-filter mode.
  const isConfirmedActive = matchesPreset(
    paymentFilter,
    statusFilter,
    CONFIRMED_PAYMENT,
    CONFIRMED_STATUS,
  );
  const isPendingRefundActive = matchesPreset(
    paymentFilter,
    statusFilter,
    REFUND_ONLY_PAYMENT,
    NO_RESTRICTION_STATUS,
  );
  // Pending registrations are people still coming who owe payment — those
  // who cancelled before paying have their own Cancelled (unpaid) preset.
  const isPendingRegistrationsActive = matchesPreset(
    paymentFilter,
    statusFilter,
    PENDING_ONLY_PAYMENT,
    CONFIRMED_STATUS,
  );
  const isCancelledUnpaidActive = matchesPreset(
    paymentFilter,
    statusFilter,
    PENDING_ONLY_PAYMENT,
    CANCELLED_STATUS,
  );

  // This page has no "page" param to reset — resetPage: false says so
  // explicitly rather than relying on the no-op of deleting a key that was
  // never set.
  const { search, setSearch } = useUrlSearchSync(searchParams, setSearchParams, {
    resetPage: false,
  });
  const { hasFilters, clearFilters } = urlFilterState(searchParams, setSearchParams, FILTER_KEYS);

  const setParam = (key: string, value: string | null) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value !== null) next.set(key, value);
      else next.delete(key);
      return next;
    });
  };

  /** Writes a multi-select Set back to the URL as a comma list, or deletes
   * the param entirely when the selection is empty ("Any"). */
  const setMultiParam = <T extends string>(key: string, values: Set<T>) => {
    setSearchParams((prev) => applyMultiParam(prev, key, values));
  };

  const applyToggleFilter = (value: ToggleFilter) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (toggleFilter === value) next.delete("quick");
      else next.set("quick", value);
      return next;
    });
  };

  /** Sets Status+Payment to an exact preset in one atomic URL update — used
   * by the Status/Payment preset shortcut buttons below. Not a toggle in the sense of alternating between two
   * arbitrary states: clicking while inactive applies the preset (writing
   * real params), and clicking again while active releases back to "no
   * restriction" — which, since that's this page's only default, means the
   * params get deleted rather than written out empty. Same as Clear filters
   * would do to these two params specifically. */
  const applyStatusPaymentPreset = (
    targetPayment: Set<PaymentStatus>,
    targetStatus: Set<AttendeeStatus>,
    isActive: boolean,
  ) => {
    setSearchParams((prev) => {
      let next = applyMultiParam(
        prev,
        "payment",
        isActive ? NO_RESTRICTION_PAYMENT : targetPayment,
      );
      next = applyMultiParam(next, "status", isActive ? NO_RESTRICTION_STATUS : targetStatus);
      return next;
    });
  };

  // --- Data ---
  const { data: eventData } = trpc.admin.events.get.useQuery({ id: id! }, { enabled: !!id });
  const { data: attendees, isLoading } = trpc.admin.events.getAttendees.useQuery(
    { eventId: id! },
    { enabled: !!id },
  );

  const pendingRefundCount = (attendees ?? []).filter(
    (a) => a.payment_status === PaymentStatus.RefundRequested,
  ).length;
  const pendingRegistrationsCount = (attendees ?? []).filter(
    (a) => a.payment_status === PaymentStatus.Pending && a.status === AttendeeStatus.Yes,
  ).length;

  function invalidateAttendees() {
    void utils.admin.events.getAttendees.invalidate({ eventId: id! });
    // Other pages (admin home dashboard, the event's own attendees summary
    // card) still read these separately — keep them fresh even though this
    // page no longer queries them itself.
    void utils.admin.rsvps.listActionRequired.invalidate({ eventId: id! });
    void utils.admin.rsvps.listPendingByAllEvents.invalidate();
  }

  // --- Mutations ---
  const confirmPaymentMutation = trpc.admin.rsvps.confirmPayment.useMutation({
    onSuccess: () => {
      invalidateAttendees();
      toast.success("Payment confirmed.");
    },
    onError: (err) => toast.error(err.message ?? "Failed to confirm payment."),
  });

  const confirmBulkPaymentMutation = trpc.admin.rsvps.confirmBulkPayment.useMutation({
    onSuccess: () => {
      setSelectedIds(new Set());
      invalidateAttendees();
      toast.success("Payments confirmed.");
    },
    onError: (err) => toast.error(err.message ?? "Failed to confirm payments."),
  });

  const adminCancelMutation = trpc.admin.rsvps.adminCancel.useMutation({
    onSuccess: () => {
      invalidateAttendees();
      toast.success("Registration cancelled.");
    },
    onError: (err) => {
      toast.error(err.message ?? "Failed to cancel registration.");
    },
  });

  const processRefundMutation = trpc.admin.rsvps.processRefund.useMutation({
    onSuccess: () => {
      invalidateAttendees();
      toast.success("Refund processed.");
    },
    onError: (err) => {
      toast.error(err.message ?? "Failed to process refund.");
    },
  });

  const undoPaymentConfirmationMutation = trpc.admin.rsvps.undoPaymentConfirmation.useMutation({
    onSuccess: () => {
      invalidateAttendees();
      toast.success("Payment confirmation undone. Status reverted to pending.");
    },
    onError: (err) => {
      toast.error(err.message ?? "Failed to undo payment confirmation.");
    },
  });

  const deleteRsvpMutation = trpc.admin.rsvps.deleteRsvp.useMutation({
    onSuccess: () => {
      invalidateAttendees();
      toast.success("RSVP deleted.");
    },
    onError: (err) => {
      toast.error(err.message ?? "Failed to delete RSVP.");
    },
  });

  // --- Handlers ---
  const handleViewHistory = (rsvpId: string, name: string) => {
    setHistoryTarget({ rsvpId, name });
  };

  const attendeeName = (a: EventAttendeeAdmin) =>
    a.member?.name ?? a.contact?.display_name ?? "this person";

  const handleConfirmPayment = async (attendee: EventAttendeeAdmin) => {
    const result = await confirmPayment(`Confirm payment for ${attendeeName(attendee)}?`);
    if (result === "cancel") return;
    confirmPaymentMutation.mutate({
      rsvpId: attendee.id,
      send_receipt: result === "send_receipt",
    });
  };

  const handleBulkConfirmPayment = async () => {
    if (selectedIds.size === 0) return;
    const result = await confirmPayment(
      `Confirm payment for ${selectedIds.size} registration${selectedIds.size === 1 ? "" : "s"}?`,
    );
    if (result === "cancel") return;
    confirmBulkPaymentMutation.mutate({
      rsvpIds: [...selectedIds],
      send_receipt: result === "send_receipt",
    });
  };

  const handleCancelRegistration = async (attendee: EventAttendeeAdmin) => {
    const name = attendeeName(attendee);
    const message =
      attendee.payment_status === PaymentStatus.Confirmed
        ? `Cancel ${name}'s registration and flag their payment for refund?`
        : `Cancel ${name}'s registration?`;
    if (await confirm(message)) {
      adminCancelMutation.mutate({ rsvpId: attendee.id });
    }
  };

  const handleProcessRefund = async (rsvpId: string, name: string) => {
    if (await confirm(`Confirm refund for ${name}?`)) {
      processRefundMutation.mutate({ rsvpId });
    }
  };

  const handleUndoPaymentConfirmation = async (rsvpId: string, name: string) => {
    if (
      await confirm(
        `Undo payment confirmation for ${name}? This will revert their payment status to Pending.`,
      )
    ) {
      undoPaymentConfirmationMutation.mutate({ rsvpId });
    }
  };

  const handleDeleteRsvp = async (rsvpId: string, name: string) => {
    if (await confirm(`Permanently delete ${name}'s RSVP? This cannot be undone.`)) {
      deleteRsvpMutation.mutate({ rsvpId });
    }
  };

  const toggleSelected = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else if (next.size < BULK_PAYMENT_LIMIT) {
        next.add(id);
      }
      return next;
    });
  };

  // --- Filtering ---
  let displayed = attendees ?? [];

  if (rawQ) {
    const q = rawQ.toLowerCase();
    displayed = displayed.filter((a) =>
      (a.member?.name ?? a.contact?.display_name ?? "").toLowerCase().includes(q),
    );
  }
  if (statusFilter.size > 0) {
    displayed = displayed.filter((a) => statusFilter.has(a.status));
  }
  if (waiverFilter === "signed") {
    displayed = displayed.filter((a) => a.waiver_signed);
  } else if (waiverFilter === "unsigned") {
    displayed = displayed.filter((a) => !a.waiver_signed);
  }
  if (checkinFilter === "checked_in") {
    displayed = displayed.filter((a) => a.check_in_at != null);
  } else if (checkinFilter === "not_checked_in") {
    displayed = displayed.filter((a) => a.check_in_at == null);
  }
  if (methodFilter === "none") {
    displayed = displayed.filter((a) => a.registration_method === null);
  } else if (methodFilter) {
    displayed = displayed.filter((a) => a.registration_method === methodFilter);
  }
  if (paymentFilter.size > 0) {
    displayed = displayed.filter(
      (a) => a.payment_status !== null && paymentFilter.has(a.payment_status),
    );
  }
  if (toggleFilter === "members") {
    displayed = displayed.filter((a) => a.is_member && a.status === AttendeeStatus.Yes);
  } else if (toggleFilter === "first_time") {
    displayed = displayed.filter((a) => a.is_first_event);
  }

  displayed = [...displayed].sort((a, b) => {
    if (sortBy === "name") {
      const nameA = (a.member?.name ?? a.contact?.display_name ?? "").toLowerCase();
      const nameB = (b.member?.name ?? b.contact?.display_name ?? "").toLowerCase();
      return nameA.localeCompare(nameB);
    }
    // Reverse chronological — most recently registered first. Attendees
    // without a created_at (shouldn't normally happen, but the field is
    // nullable) sort to the end rather than floating to the top.
    if (a.created_at === null && b.created_at === null) return 0;
    if (a.created_at === null) return 1;
    if (b.created_at === null) return -1;
    return b.created_at.localeCompare(a.created_at);
  });

  const eventType = (eventData?.event_type as EventType | null) ?? null;
  // One place decides whether this event sells days, and which days those are —
  // the same shared predicate the wizard and the server use.
  const dayPassDays =
    eventData && isDayPassAvailable(eventData)
      ? enumerateEventDays(eventData.start_date, eventData.end_date)
      : [];
  // The post-event email only makes sense once the event is over — same
  // day-based predicate the member-facing pages use. Missing status is
  // treated as Draft (never past), so the action stays hidden until the
  // event has actually loaded and qualifies.
  const eventIsPast =
    !!eventData &&
    isEventPast({
      status: eventData.status ?? EventStatus.Draft,
      start_date: eventData.start_date,
      end_date: eventData.end_date,
    });

  // One declarative list drives every "Quick:" button below instead of five
  // hand-copied <Button> blocks. They're not all the same underlying
  // mechanism — Confirmed/Pending registrations/Cancelled (unpaid)/Pending
  // refund set explicit Status+Payment params (see applyStatusPaymentPreset), while Attending
  // members/First-timers toggle a single independent `quick` param — but
  // presenting them through one shape keeps that difference contained to
  // each entry's own onClick rather than duplicated across the JSX.
  const quickFilterButtons: {
    key: string;
    label: string;
    icon: ReactNode;
    isActive: boolean;
    count?: number;
    onClick: () => void;
  }[] = [
    {
      key: "confirmed",
      label: "Confirmed",
      icon: <CheckCircle className="size-3.5" />,
      isActive: isConfirmedActive,
      onClick: () =>
        applyStatusPaymentPreset(CONFIRMED_PAYMENT, CONFIRMED_STATUS, isConfirmedActive),
    },
    {
      key: "members",
      label: "Attending members",
      icon: <Users className="size-3.5" />,
      isActive: toggleFilter === "members",
      onClick: () => applyToggleFilter("members"),
    },
    {
      key: "first_time",
      label: "First-timers",
      icon: null,
      isActive: toggleFilter === "first_time",
      onClick: () => applyToggleFilter("first_time"),
    },
    {
      key: "pending_registrations",
      label: "Pending registrations",
      icon: <DollarSign className="size-3.5" />,
      isActive: isPendingRegistrationsActive,
      count: pendingRegistrationsCount,
      onClick: () =>
        applyStatusPaymentPreset(
          PENDING_ONLY_PAYMENT,
          CONFIRMED_STATUS,
          isPendingRegistrationsActive,
        ),
    },
    {
      // No count badge: the badge flags work to do, and a cancellation that
      // never paid needs nothing further.
      key: "cancelled_unpaid",
      label: "Cancelled (unpaid)",
      icon: <XCircle className="size-3.5" />,
      isActive: isCancelledUnpaidActive,
      onClick: () =>
        applyStatusPaymentPreset(PENDING_ONLY_PAYMENT, CANCELLED_STATUS, isCancelledUnpaidActive),
    },
    {
      key: "pending_refund",
      label: "Pending refund",
      icon: <RotateCcw className="size-3.5" />,
      isActive: isPendingRefundActive,
      count: pendingRefundCount,
      onClick: () =>
        applyStatusPaymentPreset(REFUND_ONLY_PAYMENT, NO_RESTRICTION_STATUS, isPendingRefundActive),
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate(`/admin/events/${id}`)}>
          <ArrowLeft className="size-4" />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold tracking-tight">Attendees</h1>
          {eventData && <p className="text-sm text-muted-foreground">{eventData.name}</p>}
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline">
              Actions
              <ChevronDown className="size-4 ml-2" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => setAddAttendeeOpen(true)}>
              <UserPlus className="size-4" />
              Add Attendee
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setEmailAttendeesOpen(true)}>
              <Mail className="size-4" />
              Email Attendees
            </DropdownMenuItem>
            {eventIsPast && (
              <DropdownMenuItem onClick={() => setPostEventEmailOpen(true)}>
                <Send className="size-4" />
                Send post-event email
              </DropdownMenuItem>
            )}
            <DropdownMenuItem onClick={() => downloadAttendeesCsv(displayed, eventData?.name)}>
              <Download className="size-4" />
              Download CSV
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <p className="text-sm text-muted-foreground">
        Add or remove attendees, update RSVP status, track payment status, and manage refund
        requests.
      </p>

      {/* Filter bar */}
      <div className="sticky top-14 z-10 flex flex-col gap-3 border-b border-border/50 bg-background pb-3 pt-3 sm:flex-row sm:flex-wrap sm:items-center sm:gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
          <Input
            placeholder="Search by name..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <MultiSelectFilter
          label="status"
          pluralLabel="statuses"
          widthClassName="w-[130px]"
          options={[
            { value: AttendeeStatus.Yes, label: "Confirmed" },
            { value: AttendeeStatus.No, label: "Cancelled" },
          ]}
          selected={statusFilter}
          onChange={(next) => setMultiParam("status", next)}
        />
        <Select
          value={waiverFilter ?? "all"}
          onValueChange={(v) => setParam("waiver", v === "all" ? null : v)}
        >
          <SelectTrigger className="w-[130px]">
            <SelectValue placeholder="Waiver" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Any waiver</SelectItem>
            <SelectItem value="signed">Signed</SelectItem>
            <SelectItem value="unsigned">Not signed</SelectItem>
          </SelectContent>
        </Select>
        <Select
          value={checkinFilter ?? "all"}
          onValueChange={(v) => setParam("checkin", v === "all" ? null : v)}
        >
          <SelectTrigger className="w-[140px]">
            <SelectValue placeholder="Check-in" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Any check-in</SelectItem>
            <SelectItem value="checked_in">Checked in</SelectItem>
            <SelectItem value="not_checked_in">Not checked in</SelectItem>
          </SelectContent>
        </Select>
        <Select
          value={methodFilter ?? "all"}
          onValueChange={(v) => setParam("method", v === "all" ? null : v)}
        >
          <SelectTrigger className="w-[140px]">
            <SelectValue placeholder="Method" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Any method</SelectItem>
            <SelectItem value="auth">Self</SelectItem>
            <SelectItem value="admin">Admin</SelectItem>
            <SelectItem value="none">None</SelectItem>
          </SelectContent>
        </Select>
        <MultiSelectFilter
          label="payment"
          pluralLabel="payments"
          anyLabel="Any payment status"
          widthClassName="w-[140px]"
          options={[
            { value: PaymentStatus.NotRequired, label: "N/A" },
            { value: PaymentStatus.Pending, label: "Pending" },
            { value: PaymentStatus.Confirmed, label: "Paid" },
            { value: PaymentStatus.RefundRequested, label: "Refund req." },
            { value: PaymentStatus.Refunded, label: "Refunded" },
          ]}
          selected={paymentFilter}
          onChange={(next) => setMultiParam("payment", next)}
        />
        <Select value={sortBy} onValueChange={(v) => setParam("sort", v === "recent" ? null : v)}>
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder="Sort" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="recent">Most recent</SelectItem>
            <SelectItem value="name">Name (A–Z)</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Quick filters */}
      <div className="flex flex-wrap items-center gap-2 py-3">
        <span className="text-xs text-muted-foreground">Quick:</span>
        {quickFilterButtons.map((qf) => (
          <Button
            key={qf.key}
            variant={qf.isActive ? "default" : "outline"}
            size="sm"
            onClick={qf.onClick}
          >
            {qf.icon}
            {qf.label}
            {qf.count !== undefined && qf.count > 0 && (
              <span className="ml-1.5 inline-flex size-4 items-center justify-center rounded-full bg-destructive text-[10px] font-bold text-destructive-foreground">
                {qf.count > 9 ? "9+" : qf.count}
              </span>
            )}
          </Button>
        ))}
        <Button
          variant="ghost"
          size="sm"
          className="ml-auto text-muted-foreground"
          disabled={!hasFilters}
          onClick={clearFilters}
        >
          Clear filters
        </Button>
      </div>

      {/* Bulk actions */}
      {selectedIds.size > 0 && (
        <div className="flex items-center gap-3 rounded-lg border bg-muted/50 px-4 py-2">
          <span className="text-sm font-medium">{selectedIds.size} selected</span>
          <Button
            size="sm"
            variant="outline"
            onClick={handleBulkConfirmPayment}
            disabled={confirmBulkPaymentMutation.isPending}
          >
            <CheckCircle className="size-3.5" />
            Confirm Payment
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setSelectedIds(new Set())}>
            Clear
          </Button>
        </div>
      )}

      {isLoading ? (
        <p className="text-sm text-muted-foreground py-6 text-center">Loading attendees...</p>
      ) : (
        <AttendeesTable
          data={displayed}
          isWebmaster={isWebmaster}
          selectedIds={selectedIds}
          onToggleSelect={toggleSelected}
          onToggleSelectAllPending={() => {
            const pending = displayed.filter((a) => a.payment_status === PaymentStatus.Pending);
            const allSelected = pending.every((a) => selectedIds.has(a.id));
            setSelectedIds((prev) => {
              const next = new Set(prev);
              if (allSelected) {
                pending.forEach((a) => next.delete(a.id));
              } else {
                pending.slice(0, BULK_PAYMENT_LIMIT).forEach((a) => next.add(a.id));
              }
              return next;
            });
          }}
          onViewHistory={handleViewHistory}
          onEdit={setEditTarget}
          onConfirmPayment={handleConfirmPayment}
          onSendReminder={setReminderTarget}
          onCancelRegistration={handleCancelRegistration}
          onProcessRefund={handleProcessRefund}
          onUndoPaymentConfirmation={handleUndoPaymentConfirmation}
          onDeleteRsvp={handleDeleteRsvp}
        />
      )}

      {historyTarget && (
        <HistoryDialog
          rsvpId={historyTarget.rsvpId}
          name={historyTarget.name}
          open
          onOpenChange={(open) => {
            if (!open) setHistoryTarget(null);
          }}
        />
      )}

      {editTarget && (
        <EditAttendeeDialog
          attendee={editTarget}
          eventId={id!}
          eventType={eventType}
          dayPassDays={dayPassDays}
          open
          onOpenChange={(open) => {
            if (!open) setEditTarget(null);
          }}
          onSaved={() => {
            void utils.admin.events.getAttendees.invalidate({ eventId: id! });
          }}
        />
      )}

      {reminderTarget && (
        <SendPaymentReminderDialog
          rsvp={{
            id: reminderTarget.id,
            displayName: attendeeName(reminderTarget),
            paymentAmountCents: reminderTarget.payment_amount_cents,
            paymentMethod: reminderTarget.payment_method,
            contactEmail: reminderTarget.contact?.email ?? null,
          }}
          eventName={eventData?.name}
          open
          onOpenChange={(open) => {
            if (!open) setReminderTarget(null);
          }}
        />
      )}

      <AddAttendeeDialog
        eventId={id!}
        eventType={eventType}
        dayPassDays={dayPassDays}
        existingContactIds={
          new Set(
            (attendees ?? []).map((a) => a.contact_id).filter((cid): cid is string => cid !== null),
          )
        }
        open={addAttendeeOpen}
        onOpenChange={setAddAttendeeOpen}
        onAdded={() => utils.admin.events.getAttendees.invalidate({ eventId: id! })}
      />

      <EmailAttendeesDialog
        eventId={id!}
        attendees={attendees ?? []}
        open={emailAttendeesOpen}
        onOpenChange={setEmailAttendeesOpen}
      />

      <SendPostEventEmailDialog
        eventId={id!}
        open={postEventEmailOpen}
        onOpenChange={setPostEventEmailOpen}
      />

      {confirmDialog}
      {confirmPaymentDialog}
    </div>
  );
}
